import { _ } from "../../../polyglot/polyglot.js";
import { Widget } from "@foretagsplatsen/widgetjs";
import KeyFigureCategory from "../../../../models/keyFigure/KeyFigureCategory.js";
import keyFigureCategoryPickerWidget from "../keyFigureCategoryPickerWidget.js";
import keyFigurePickerContentWidget from "../keyFigurePickerContentWidget.js";
import SearchFieldWidget from "../../SearchFieldWidget/SearchFieldWidget.js";

/**
 * `KeyFigureSelectionWidget` is an intuitive way of selecting a key figure.
 * It renders a category dropdown, a search field, and a list of key figures sorted
 * by category.
 *
 * @param {KeyFigure[]} keyFigures - List of key figures to select from
 * @param {bool} [multipleSelection] - If true, the widget will make
 *   it possible for the user to select more than one key figure
 * @param {function} [onItemSelected] - Callback taking the clicked item as argument
 * @param {function} [checkIfEnabled] - Function checking if the item
 *   should be disabled. It takes a keyFigure item as parameter and
 *   returns an object containing an `isEnabled` boolean and
 *   `disabledTitle` to give information to the user on why the item
 *   is disabled
 */
export default class KeyFigureSelectionWidget extends Widget {
	constructor({
		keyFigures,
		multipleSelection = false,
		onItemSelected = (_item) => {},
		checkIfEnabled = (_item) => ({ isEnabled: true }),
	} = {}) {
		super(...arguments);

		this._categoryPicker = keyFigureCategoryPickerWidget({
			isVisiblePredicate: (categories) => categories.length,
			pickerId: `categoryPickerId${this.getId()}`,
		});

		this._pickerContent = keyFigurePickerContentWidget({
			multipleSelection,
			checkIfEnabled,
		});

		this._pickerContent.onItemClicked.register(onItemSelected);

		this._categoryPicker.onChange((category) =>
			this._pickerContent.showCategory(category),
		);

		if (keyFigures?.length) {
			this.setup({ keyFigures });
		}

		this._searchField = new SearchFieldWidget({
			inputId: `pickerSearchField${this.getId()}`,
			inputClass: "form-control",
			placeholder: _("Search…"),
		});

		this._searchField.applySearchOn(this._pickerContent);
	}

	setup({ keyFigures, presets }) {
		const categories = this._makeCategories({ keyFigures, presets });

		this._categoryPicker.setCategories(categories);
		this._pickerContent.setCategories(categories);
	}

	getSelectedItems() {
		return this._pickerContent.getSelectedItems();
	}

	selectKeyFigure(keyFigure) {
		this._pickerContent.selectKeyFigure(keyFigure);
	}

	getSelection() {
		return this._pickerContent.getSelectedItems()[0];
	}

	selectAll() {
		this._pickerContent.selectAll();
	}

	hasSelection() {
		return !this._pickerContent.isSelectionEmpty();
	}

	renderContentOn(html) {
		html.render(
			this._categoryPicker,
			this._searchField,
			this._pickerContent,
		);
	}

	_makeCategories({ keyFigures = [], presets = [] }) {
		const presetCategories = KeyFigureCategory.fromPresets(presets);

		return [
			...KeyFigureCategory.createCategoriesFromKeyFigures(keyFigures),
			...Object.values(presetCategories),
		];
	}
}
